import * as NGL from "ngl";
import React, {useCallback, useContext, useMemo, useState} from "react";
import {Alert, Button, Card, Col, Dropdown, Radio, Row, Tag, Typography} from "antd";
import {DownOutlined, MoonFilled, SunOutlined} from "@ant-design/icons";
import {ConfigContext} from "./App";


const filterResidues = function (conformation, data, structure, lowerBound, upperBound) {
    const key = conformation
    const selectedOutlierIndices = {}
    const selectedAllowedIndices = {}
    data.forEach(element => {
        if ((key in element) && element[key].compression) {
            if (element[key].compression < lowerBound) {
                selectedOutlierIndices[`${element.chain}:${element.index}`] = true
            } else if (element[key].compression < upperBound) {
                selectedAllowedIndices[`${element.chain}:${element.index}`] = true
            }
        }
    })
    structure.addRepresentation("cartoon", {
        color: NGL.ColormakerRegistry.addScheme(function (params) {
            this.atomColor = function (ap) {
                const resIdentifier = `${ap.chainname}:${ap.resno}`
                if (resIdentifier in selectedOutlierIndices) return 0xFF0000
                if (resIdentifier in selectedAllowedIndices) return 0xF6e5BB
                return 0x00FF00
            }
        })
    });
}

function MolecularVisualizer({pdb_id, data, dynamicColumns, uploadedFile}) {

    const {outlierLowerBound, allowedLowerBound, ..._} = useContext(ConfigContext);

    const [selectedConformation, setSelectedConformation] = useState("")
    const [proteinStage, setProteinStage] = useState({});
    const [structure, setStructure] = useState({})
    const [backgroundValue, setBackgroundValue] = useState(1);

    const buildDynamicColumnMenu = useMemo(() => {
        const items = dynamicColumns.joint ? [{
            key: 'joint',
            label: 'Joint'
        }] : []

        if (dynamicColumns.marginal.length > 0) {
            items.push({
                key: 'marginal',
                label: 'Marginal',
                children: dynamicColumns.marginal.map(element => {
                    return {
                        key: element.key,
                        label: element.name,
                    }
                })
            })
        }
        if (dynamicColumns.conditioned.length > 0) {
            items.push({
                key: 'conditioned',
                label: 'Conditioned',
                children: dynamicColumns.conditioned.map(element => {
                    return {
                        key: `${element.key.up}|${element.key.down}`,
                        label: `${element.name.up}|${element.name.down}`
                    }
                })
            })
        }
        return items
    }, [dynamicColumns, data])

    const stageElementRef = useCallback(async (element) => {
        if (element && pdb_id) {
            const stage = new NGL.Stage(element);
            stage.removeAllComponents()
            const pdbFile = uploadedFile ? uploadedFile : `https://files.rcsb.org/download/${pdb_id}.pdb`
            const component = await stage.loadFile(pdbFile)
            component.addRepresentation("cartoon", {color: 0x00FF00});
            component.autoView();

            const tooltip = stage.viewer.container.children.item(1); //TODO can break

            stage.mouseControls.remove("hoverPick")

            stage.signals.hovered.add(function (pickingProxy) {
                if (pickingProxy && (pickingProxy.atom || pickingProxy.bond)) {
                    const atom = pickingProxy.atom || pickingProxy.closestBondAtom;
                    tooltip.textContent = "RESIDUE: " + atom.chainname + ":" + atom.resname + ":" + atom.resno
                    tooltip.style.zIndex = 10000
                    tooltip.style.bottom = window.innerHeight - pickingProxy.mouse.position.y + 3 + "px"
                    tooltip.style.left = pickingProxy.mouse.position.x + 3 + "px"
                    tooltip.style.display = "block";
                } else {
                    tooltip.style.display = "none";
                }
            });
            setStructure(component)
            setProteinStage(stage)

            const columns = buildDynamicColumnMenu
            if (columns.length > 0) {
                filterResidues(columns[0].children ? columns[0].children[0].key:columns[0].key, data, component, outlierLowerBound, allowedLowerBound)
                setSelectedConformation(columns[0].children ? columns[0].children[0].label:columns[0].label)
            }
        }
    }, [pdb_id, uploadedFile, buildDynamicColumnMenu]);


    return (
        <Card style={{overflowX:"auto", paddingLeft:10, paddingRight:10 ,scrollbarWidth:'thin',}}>
            <div style={{minWidth: "1400px"}} >
                { (data?.length > 0) && (dynamicColumns.joint || dynamicColumns.marginal.length > 0 || dynamicColumns.conditioned.length > 0) ?
                    <>
                        <Row>
                            <Col span={8} style={{display: 'flex', justifyContent: 'flex-start'}}>
                                <Dropdown.Button

                                    disabled={data.length === 0}
                                    buttonsRender={
                                        (elements) => [
                                            <Button disabled={data.length === 0} style={{pointerEvents: "none"}}> Select a
                                                Conformation</Button>,
                                            elements[1]
                                        ]
                                    }
                                    icon={<DownOutlined/>}
                                    menu={{
                                        items: buildDynamicColumnMenu,
                                        onClick: (event) => {
                                            const item = event.keyPath.reverse().reduce(
                                                (filterData, key) => {
                                                    const foundItem = filterData.find(item => item.key === key)
                                                    return foundItem?.children || foundItem
                                                }, buildDynamicColumnMenu)
                                            setSelectedConformation(item.label)
                                            filterResidues(event.key, data, structure, outlierLowerBound, allowedLowerBound)
                                        },

                                    }}>
                                </Dropdown.Button>
                            </Col>
                            <Col span={8} style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                {data.length > 0 &&
                                    <Typography.Title level={5} style={{margin: 0}}>
                                        {selectedConformation ? <span>Outlier and Allowed instances for the conformation type: <span
                                            style={{fontSize: "larger"}}>{selectedConformation} </span></span> : "View Outlier and Allowed instances on the structure"}
                                    </Typography.Title>
                                }
                            </Col>
                            <Col span={8} style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                                <Tag style={{fontSize: "1.0em", color: 'black', fontWeight: "inherit"}}
                                     bordered={false} color={'white'}>Background</Tag>
                                <Radio.Group onChange={(e) => {
                                    const value = e.target.value
                                    if (value === 1) {
                                        proteinStage.viewer.setBackground('black')
                                    } else {
                                        proteinStage.viewer.setBackground('white')
                                    }
                                    setBackgroundValue(value)
                                }} value={backgroundValue}>
                                    <Radio value={1} disabled={data.length === 0} defaultChecked><MoonFilled/></Radio>
                                    <Radio value={2} disabled={data.length === 0}><SunOutlined/></Radio>
                                </Radio.Group>
                            </Col>
                        </Row>
                        <br/>
                        <Row span={24} style={{display: 'flex', justifyContent: 'center'}}>
                            <div key={pdb_id} ref={stageElementRef} style={{width: "1200px", height: "800px"}}></div>
                        </Row>
                        <Row span={24} style={{marginTop:'3vh'}}>   <a  rel="noreferrer"  href="https://nglviewer.org/" target="_blank">Powered by NGL Viewer</a></Row>
                    </> :
                    <>
                        <Alert
                            style={{width:'fit-content', textAlign:"left"}}
                            message="No validation information found"
                            description="Please submit a PDB ID or upload a PDB file"
                            type="info"
                            showIcon
                        />
                    </>
                }
            </div>
        </Card>
    )
}

export default MolecularVisualizer;